<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click')"
  >
    <i
      v-if="loading"
      class="pi pi-spin pi-spinner -ml-1 mr-2"
    ></i>

    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
  variant?: "primary" | "secondary" | "danger" | "ghost" | "success";
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "md",
  type: "button",
  disabled: false,
  loading: false,
});

defineEmits<{
  click: [];
}>();

const buttonClasses = computed(() => {
  const base =
    "inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border";

  const variants = {
    primary: "bg-orange-600 hover:bg-orange-700 text-white border-transparent",
    secondary:
      "bg-gray-50 hover:bg-gray-100 text-gray-900 border-gray-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:text-gray-200 dark:border-neutral-600",
    danger: "bg-red-600 hover:bg-red-700 text-white border-transparent",
    ghost:
      "text-gray-700 hover:bg-gray-100 border-transparent dark:text-gray-300 dark:hover:bg-neutral-800",
    success: "bg-green-600 hover:bg-green-700 text-white border-transparent",
  };

  const sizes = {
    sm: "px-2 py-1 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-sm",
  };

  const disabled =
    props.disabled || props.loading ? "opacity-50 cursor-not-allowed" : "";

  return `${base} ${variants[props.variant]} ${sizes[props.size]} ${disabled}`;
});
</script>
